import React, { useEffect, useState } from "react";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Alert,
  LinearProgress,
} from "@mui/material";
import { useDispatch } from "react-redux";
import { updateScreenTitle } from "../../../LayoutContainers/AppLayout/redux/AppSlice";
import WDBox from "../../../MaterialComponents/WDBox";
import AppService from "../../../Configurations/Appservice";

const CommissionByService = () => {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(updateScreenTitle("Commission by Service"));
    return () => dispatch(updateScreenTitle(""));
  }, []);

  useEffect(() => {
    fetchCommissionByService();
  }, []);

  const fetchCommissionByService = async () => {
    try {
      setLoading(true);
      const authInfo = localStorage.getItem("authInfo");
      const parsedAuth = JSON.parse(authInfo);
      const partnerId = parsedAuth.principalInfo.organisationId;

      const response = await AppService.get(
        `api/channel-partners/${partnerId}/commission/by-service`
      );
      setServices(response || []);
      setError(null);
    } catch (err) {
      console.error("Error fetching commission by service:", err);
      // Use mock data
      const mockServices = [
        { serviceName: "Visa Renewal", ticketsCount: 8, commissionRate: 22.0, totalCommission: "88.00" },
        { serviceName: "Emirates ID Renewal", ticketsCount: 12, commissionRate: 25.0, totalCommission: "90.00" },
        { serviceName: "Trade License", ticketsCount: 5, commissionRate: 18.0, totalCommission: "82.00" },
        { serviceName: "PRO Services", ticketsCount: 3, commissionRate: 20.0, totalCommission: "45.00" },
      ];
      setServices(mockServices);
      setError(null);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={3}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  const totalCommission = services.reduce((sum, service) =>
    sum + parseFloat(service.totalCommission || 0), 0
  );

  const totalTickets = services.reduce((sum, service) =>
    sum + (service.ticketsCount || 0), 0
  );

  const maxCommission = Math.max(...services.map(s => parseFloat(s.totalCommission || 0)));

  return (
    <WDBox>
      <TableContainer component={Paper}>
        <Table>
          <TableHead sx={{ backgroundColor: "#e0e0e0" }}>
            <TableRow>
              <TableCell sx={{ fontWeight: "bold", color: "#000", fontSize: "14px", padding: "12px" }}>Service Name</TableCell>
              <TableCell align="right" sx={{ fontWeight: "bold", color: "#000", fontSize: "14px", padding: "12px" }}>
                Tickets
              </TableCell>
              <TableCell align="right" sx={{ fontWeight: "bold", color: "#000", fontSize: "14px", padding: "12px" }}>
                Commission Rate
              </TableCell>
              <TableCell align="right" sx={{ fontWeight: "bold", color: "#000", fontSize: "14px", padding: "12px" }}>
                Commission Earned
              </TableCell>
              <TableCell align="center" sx={{ fontWeight: "bold", color: "#000", fontSize: "14px", padding: "12px" }}>
                Distribution
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {services.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} align="center" sx={{ py: 3 }}>
                  <Alert severity="info">No commission data available</Alert>
                </TableCell>
              </TableRow>
            ) : (
              services.map((service, index) => {
                const commissionValue = parseFloat(service.totalCommission || 0);
                const percentage = (commissionValue / maxCommission) * 100;

                return (
                  <TableRow key={index} hover>
                    <TableCell sx={{ fontWeight: "500" }}>{service.serviceName}</TableCell>
                    <TableCell align="right">{service.ticketsCount}</TableCell>
                    <TableCell align="right">{service.commissionRate}%</TableCell>
                    <TableCell align="right" sx={{ fontWeight: "500" }}>
                      AED {commissionValue.toFixed(2)}
                    </TableCell>
                    <TableCell align="center">
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <LinearProgress
                          variant="determinate"
                          value={percentage}
                          sx={{ flex: 1, height: 8, borderRadius: 4 }}
                        />
                        <span style={{ fontSize: "12px", minWidth: "35px" }}>
                          {Math.round(percentage)}%
                        </span>
                      </Box>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {services.length > 0 && (
        <Box sx={{ mt: 3, p: 2, background: "#f9f9f9", borderRadius: 1 }}>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "20px" }}>
            <div>
              <strong>Total Services:</strong> {services.length}
            </div>
            <div>
              <strong>Total Tickets:</strong> {totalTickets}
            </div>
            <div>
              <strong>Total Commission:</strong> AED {totalCommission.toFixed(2)}
            </div>
          </div>

          {/* Top Service */}
          {services.length > 0 && (
            <Box sx={{ mt: 2, p: 2, background: "white", borderRadius: 1, borderLeft: "4px solid #1a5c5c" }}>
              <strong>Top Service:</strong>{" "}
              {services.reduce((prev, curr) =>
                parseFloat(curr.totalCommission || 0) > parseFloat(prev.totalCommission || 0) ? curr : prev
              ).serviceName}
            </Box>
          )}
        </Box>
      )}
    </WDBox>
  );
};

export default CommissionByService;
